import { Component, ElementRef, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { MenuItemProps } from '@bl/components';
import FileSaver from 'file-saver';
import { Observable, Subscription, forkJoin, of } from 'rxjs';
import { finalize, map, switchMap } from 'rxjs/operators';
import { Client } from 'src/app/core/entity/client';
import {
  LundifySharedDocument,
  LundifySharedDocumentPage,
  LundifySharedDocumentStatusBadge,
} from 'src/app/core/entity/lundify-shared-document';
import { LundifySharedDocumentStatus } from 'src/app/core/entity/lundify-shared-document-status.enum';
import { DocumentService } from 'src/app/core/services/document.service';
import { Store } from '@ngrx/store';
import { AppState } from '@app/core/state/appState';
import { ToastActions } from 'src/app/core/state/toast/toast.actions';

type SharedFolderKey = 'outgoing' | 'incoming' | 'archived';

interface SharedFolder {
  key: SharedFolderKey;
  name: string;
  icon: string;
}

// A sub-folder row shown above the documents of the current "Outgoing" path.
interface SharedSubfolderRow {
  isFolder: true;
  folderName: string;
  fullPath: string;
  // True when no shared document lives in the folder (or below), so it can be removed.
  deletable: boolean;
}

type SharedRow = LundifySharedDocument | SharedSubfolderRow;

// Virtual folder structure for Shared documents:
// - "Outgoing"/"Incoming" show the non-archived documents (providerLifecycleStatus=ACTIVE) for that
//   direction (docs/design/workflow-shared-documents-api.md, section 4, direction=OUTBOUND/INBOUND).
// - Sub-folders exist only under "Outgoing" and are just the share's "path" field
//   (docs/handoff/handoff-bla-shared-documents-provider-folders-api.md). Folders that hold documents
//   are derived from the documents' path; BLA stores no empty folders, so folders created without
//   a document are kept in Workflow's own table (clients_shared_folders).
// - "Archived" is a flat list combining both directions (providerLifecycleStatus=ARCHIVED), filtered
//   server-side. Archiving is Workflow-only (BLA provider_lifecycle_status), irreversible, and does
//   not affect what Lundify users see.
@Component({
  selector: 'app-file-browser-shared-section',
  templateUrl: './file-browser-shared-section.component.html',
  providers: [DocumentService],
})
export class FileBrowserSharedSectionComponent implements OnChanges {
  // Not static: the input only exists in the DOM once a folder is opened (*ngIf="currentFolder"),
  // so the query must be re-resolved after view checks instead of once at init.
  @ViewChild('shareFileInput') shareFileInput?: ElementRef<HTMLInputElement>;

  @Input() client: Client | undefined;

  readonly folders: SharedFolder[] = [
    { key: 'outgoing', name: 'Utgående dokument', icon: 'fa fa-share-square' },
    { key: 'incoming', name: 'Inkommande dokument', icon: 'fa fa-reply-all' },
    { key: 'archived', name: 'Arkiverade dokument', icon: 'fa fa-archive' },
  ];

  // Bound to p-table's [columns] input so PrimeNG can keep the scrollable header and body
  // tables' widths in sync (see internal-section for the same pattern) - fixes the emptymessage
  // row not spanning the full table width.
  readonly documentColumns = [{ field: 'fileName' }, { field: 'sharedAt' }, { field: 'status' }, { field: 'actions' }];

  currentFolder: SharedFolder | null = null;
  // Slash-separated sub-path inside "Outgoing" ('' = root of Outgoing).
  currentPath = '';
  // Empty folders stored in Workflow (full paths).
  private virtualFolders: string[] = [];
  rows: SharedRow[] = [];
  loading = false;
  private listSubscription: Subscription | null = null;
  private badgeCache = new WeakMap<LundifySharedDocument, LundifySharedDocumentStatusBadge>();
  private menuItemsCache = new WeakMap<LundifySharedDocument, MenuItemProps[]>();
  private _documents: LundifySharedDocument[] = [];
  private _searchQuery = '';
  newFolderName = '';

  get documents(): LundifySharedDocument[] {
    return this._documents;
  }
  set documents(value: LundifySharedDocument[]) {
    this._documents = value;
    this.rebuildRows();
  }

  get searchQuery(): string {
    return this._searchQuery;
  }
  set searchQuery(value: string) {
    this._searchQuery = value;
    this.rebuildRows();
  }

  // Files picked via the button or dropped on the table, waiting for the share overlay.
  // BLA shares one file per request, so they are processed one at a time.
  pendingFiles: File[] = [];
  fileToShare: File | null = null;
  shareDialogVisible = false;
  // Set when the dialog manages access of a document that was already sent, not a fresh upload.
  documentToManage: LundifySharedDocument | null = null;
  sharePath = '';
  isDragOver = false;

  constructor(private documentService: DocumentService, private store: Store<AppState>) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.client) {
      this.listSubscription?.unsubscribe();
      this.listSubscription = null;
      this.loading = false;
      this.currentFolder = null;
      this.currentPath = '';
      this.virtualFolders = [];
      this.documents = [];
      this.searchQuery = '';
      this.pendingFiles = [];
      this.fileToShare = null;
      this.shareDialogVisible = false;
      this.documentToManage = null;
    }
  }

  isFolderRow(row: SharedRow): row is SharedSubfolderRow {
    return (row as SharedSubfolderRow).isFolder === true;
  }

  // Sub-folders and documents of the current path (Outgoing), or all documents (other folders),
  // narrowed by the search query. Rebuilt only when its inputs change: returning a fresh array on
  // every change detection cycle would keep re-rendering the table.
  private rebuildRows() {
    const query = this._searchQuery.trim().toLowerCase();
    const matches = (name: string) => !query || name.toLowerCase().includes(query);

    if (this.currentFolder?.key !== 'outgoing') {
      this.rows = this._documents.filter((document) => matches(document.fileName));
      return;
    }

    const prefix = this.currentPath ? `${this.currentPath}/` : '';
    const subfolders = new Set<string>();
    const documentPaths = this._documents.map((document) => this.normalizePath(document.path));
    const paths = [...documentPaths, ...this.virtualFolders];
    paths
      .filter((path) => path.startsWith(prefix) && path.length > prefix.length)
      .forEach((path) => subfolders.add(path.substring(prefix.length).split('/')[0]));

    const folderRows: SharedSubfolderRow[] = [...subfolders]
      .filter(matches)
      .sort((a, b) => a.localeCompare(b))
      .map((folderName) => {
        const fullPath = `${prefix}${folderName}`;
        const deletable = !documentPaths.some((path) => path === fullPath || path.startsWith(`${fullPath}/`));
        return { isFolder: true, folderName, fullPath, deletable };
      });
    const documentRows = this._documents.filter(
      (document) => this.normalizePath(document.path) === this.currentPath && matches(document.fileName),
    );
    this.rows = [...folderRows, ...documentRows];
  }

  private normalizePath(path: string | null | undefined): string {
    return (path ?? '')
      .split('/')
      .map((segment) => segment.trim())
      .filter((segment) => !!segment)
      .join('/');
  }

  // The API pages are fetched completely; the table paginator slices this complete set locally.
  get canShareDocuments(): boolean {
    return this.currentFolder?.key === 'outgoing';
  }

  get currentVisualPath(): string {
    if (!this.currentFolder) {
      return '/';
    }
    return `/${this.currentFolder.name}/${this.currentPath ? `${this.currentPath}/` : ''}`;
  }

  openFolder(folder: SharedFolder) {
    this.currentFolder = folder;
    this.currentPath = '';
    this.rebuildRows();
    this.loadFolder();
  }

  openSubfolder(folderName: string) {
    this.currentPath = this.currentPath ? `${this.currentPath}/${folderName}` : folderName;
    this.searchQuery = '';
  }

  upDir() {
    if (this.currentPath) {
      this.currentPath = this.currentPath.includes('/') ? this.currentPath.substring(0, this.currentPath.lastIndexOf('/')) : '';
      this.searchQuery = '';
      return;
    }

    this.listSubscription?.unsubscribe();
    this.loading = false;
    this.currentFolder = null;
    this.virtualFolders = [];
    this.documents = [];
  }

  refresh() {
    if (this.currentFolder) {
      this.loadFolder();
    }
  }

  loadFolder() {
    if (!this.client || !this.currentFolder) {
      return;
    }

    this.loading = true;
    if (this.currentFolder.key === 'archived') {
      this.loadArchived();
      return;
    }

    const clientId = this.client.id;
    const request =
      this.currentFolder.key === 'incoming'
        ? this.getAllSharedDocuments((page) => this.documentService.getIncomingSharedDocuments(clientId, 'ACTIVE', page))
        : this.getAllSharedDocuments((page) => this.documentService.getOutgoingSharedDocuments(clientId, 'ACTIVE', page));
    if (this.currentFolder.key === 'outgoing') {
      this.loadOutgoing(request);
      return;
    }
    this.loadDirection(request);
  }

  // Outgoing also needs the empty folders stored in Workflow, which are merged with the folders
  // derived from the documents' path.
  private loadOutgoing(request: Observable<LundifySharedDocument[]>) {
    if (!this.client) {
      this.loading = false;
      return;
    }

    this.subscribeToList(
      forkJoin({ documents: request, folders: this.documentService.getSharedFolders(this.client.id) }),
      ({ documents, folders }) => {
        this.virtualFolders = folders;
        this.documents = documents;
      },
    );
  }

  private getAllSharedDocuments(
    fetchPage: (page: number) => Observable<LundifySharedDocumentPage>,
  ): Observable<LundifySharedDocument[]> {
    return fetchPage(0).pipe(
      switchMap((firstPage) => {
        const remainingPages = Array.from({ length: Math.max(firstPage.totalPages - 1, 0) }, (_, index) =>
          fetchPage(index + 1),
        );
        if (remainingPages.length === 0) {
          return of(firstPage.content);
        }
        return forkJoin(remainingPages).pipe(
          map((pages) => [...firstPage.content, ...pages.flatMap((page) => page.content)]),
        );
      }),
    );
  }

  // Maps the BLA-aggregated status onto a badge label/colour. PENDING is matched by prefix because
  // the backend reports progress as "PENDING (x/y)" and that counter has to survive into the label.
  // Both statusBadge() and menuItemsFor() are bound in the row template and run on every change
  // detection cycle. Returning a fresh object/array each time makes the bl-frontend-* web
  // component inputs look changed on every cycle, which re-renders them endlessly and freezes the
  // page, so the results are cached per document until the list is reloaded.
  statusBadge(sharedDocument: LundifySharedDocument): LundifySharedDocumentStatusBadge {
    let badge = this.badgeCache.get(sharedDocument);
    if (!badge) {
      badge = this.buildStatusBadge(sharedDocument);
      this.badgeCache.set(sharedDocument, badge);
    }
    return badge;
  }

  private buildStatusBadge(sharedDocument: LundifySharedDocument): LundifySharedDocumentStatusBadge {
    const status = (sharedDocument.aggregatedStatus || '').toUpperCase();

    if (status.startsWith(LundifySharedDocumentStatus.PENDING)) {
      const fraction = status.match(/\(([^)]+)\)/);
      return { text: fraction ? `Väntar (${fraction[1]})` : 'Väntar', variant: 'info' };
    }

    switch (status) {
      case LundifySharedDocumentStatus.APPROVED:
        return { text: 'Godkänd', variant: 'success' };
      case LundifySharedDocumentStatus.READ:
        return { text: 'Läst', variant: 'success' };
      case LundifySharedDocumentStatus.NEW:
        return { text: 'Ny', variant: 'info' };
      case LundifySharedDocumentStatus.NOT_SHARED:
        return { text: 'Ej delad', variant: 'warning' };
      default:
        return { text: sharedDocument.aggregatedStatus || '-', variant: 'neutral' };
    }
  }

  // "Delete document" is not offered: BLA has no provider delete endpoint.
  menuItemsFor(sharedDocument: LundifySharedDocument): MenuItemProps[] {
    let items = this.menuItemsCache.get(sharedDocument);
    if (!items) {
      items = this.buildMenuItems(sharedDocument);
      this.menuItemsCache.set(sharedDocument, items);
    }
    return items;
  }

  private buildMenuItems(sharedDocument: LundifySharedDocument): MenuItemProps[] {
    const items: MenuItemProps[] = [
      { id: 'download', text: 'Ladda ner', icon: 'download', onAction: () => this.downloadDocument(sharedDocument) },
    ];
    if (this.canManageAccess(sharedDocument)) {
      items.push({ id: 'manage-access', text: 'Hantera åtkomst', icon: 'user-plus', onAction: () => this.manageAccess(sharedDocument) });
    }
    if (this.currentFolder?.key !== 'archived') {
      items.push({ id: 'archive', text: 'Arkivera dokument', icon: 'box-archive', onAction: () => this.archiveDocument(sharedDocument) });
    }
    return items;
  }

  triggerShareDocuments() {
    this.shareFileInput?.nativeElement.click();
  }

  onShareFilesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    this.queueFilesForSharing(Array.from(input.files ?? []));
    input.value = '';
  }

  onDragOver(event: DragEvent) {
    if (!this.canShareDocuments) {
      return;
    }
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave() {
    this.isDragOver = false;
  }

  onDrop(event: DragEvent) {
    if (!this.canShareDocuments) {
      return;
    }
    event.preventDefault();
    this.isDragOver = false;
    this.queueFilesForSharing(Array.from(event.dataTransfer?.files ?? []));
  }

  onShareDialogVisibleChange(visible: boolean) {
    this.shareDialogVisible = visible;
    if (!visible) {
      this.fileToShare = null;
      this.documentToManage = null;
      setTimeout(() => this.openNextShareDialog());
    }
  }

  // Only Workflow -> Lundify documents can have recipients added by the provider.
  canManageAccess(sharedDocument: LundifySharedDocument): boolean {
    return (
      this.canShareDocuments &&
      this.currentFolder?.key === 'outgoing' &&
      !!sharedDocument.documentShareId
    );
  }

  // Opens the dialog for an already sent document. Recipients are changed through the provider
  // recipients endpoints, so the file is not downloaded or re-sent.
  manageAccess(sharedDocument: LundifySharedDocument) {
    if (!this.client || this.shareDialogVisible || !this.canManageAccess(sharedDocument)) {
      return;
    }

    this.fileToShare = null;
    this.documentToManage = sharedDocument;
    this.sharePath = sharedDocument.path || '';
    this.shareDialogVisible = true;
  }

  onDocumentShared() {
    this.refresh();
  }

  // Folders are only the share's "path", so a folder without documents is stored in Workflow
  // until a file is shared into it.
  createFolder() {
    const name = this.newFolderName.trim();
    if (!name || !this.canShareDocuments || !this.client) {
      return;
    }

    if (name.includes('/') || name.includes('\\') || name === '.' || name === '..' || name.length > 100) {
      this.store.dispatch(
        ToastActions.showErrorMessage({
          summary: 'Ogiltigt mappnamn',
          detail: 'Mappnamnet får inte innehålla snedstreck eller vara längre än 100 tecken.',
        }),
      );
      return;
    }

    const fullPath = this.currentPath ? `${this.currentPath}/${name}` : name;
    this.documentService.createSharedFolder(this.client.id, fullPath).subscribe({
      next: (created) => {
        if (!this.virtualFolders.includes(created.path)) {
          this.virtualFolders = [...this.virtualFolders, created.path];
        }
        this.newFolderName = '';
        this.rebuildRows();
      },
      error: (error) => console.error('[SHARED-DOCS] Creating folder failed', error),
    });
  }

  private folderMenuCache = new WeakMap<SharedSubfolderRow, MenuItemProps[]>();

  // Cached per row for the same reason as menuItemsFor (see statusBadge).
  folderMenuItemsFor(row: SharedSubfolderRow): MenuItemProps[] {
    let items = this.folderMenuCache.get(row);
    if (!items) {
      items = row.deletable
        ? [{ id: 'delete-folder', text: 'Ta bort mapp', icon: 'trash-alt', onAction: () => this.deleteFolder(row) }]
        : [];
      this.folderMenuCache.set(row, items);
    }
    return items;
  }

  private deleteFolder(row: SharedSubfolderRow) {
    if (!this.client) {
      return;
    }

    this.documentService.deleteSharedFolder(this.client.id, row.fullPath).subscribe({
      next: () => {
        this.virtualFolders = this.virtualFolders.filter(
          (path) => path !== row.fullPath && !path.startsWith(`${row.fullPath}/`),
        );
        this.rebuildRows();
      },
      error: (error) => console.error('[SHARED-DOCS] Deleting folder failed', error),
    });
  }

  // Matches the mock's note: right after a file is picked or dropped, the share overlay pops up so
  // the user can choose who gets access, or upload the file without recipients and manage access
  // later ("Hantera åtkomst").
  private queueFilesForSharing(files: File[]) {
    if (files.length === 0) {
      return;
    }

    this.pendingFiles = [...this.pendingFiles, ...files];
    if (!this.shareDialogVisible) {
      this.openNextShareDialog();
    }
  }

  private openNextShareDialog() {
    if (this.shareDialogVisible) {
      return;
    }

    const next = this.pendingFiles.shift();
    if (!next) {
      return;
    }
    this.fileToShare = next;
    this.sharePath = this.currentPath;
    this.documentToManage = null;
    this.shareDialogVisible = true;
  }

  // Direction is seen from Lundify's side (workflow-shared-documents-api.md, section 4):
  // "incoming" = BLA OUTBOUND (Lundify -> Workflow), "outgoing" = BLA INBOUND (Workflow -> Lundify).
  // Polling returns "data": null, so bytes come from the file endpoint (section 6), which serves
  // both directions. Opening an "incoming" document is also confirmed so Lundify shows "Read" on
  // its next poll (section 5).
  downloadDocument(sharedDocument: LundifySharedDocument) {
    const clientId = this.client?.id;
    const direction = this.currentFolder?.key ?? null;

    if (sharedDocument.data) {
      this.saveInlineDocument(sharedDocument);
      this.confirmDocumentOpened(clientId, direction, sharedDocument);
      return;
    }

    if (clientId !== undefined && sharedDocument.reference) {
      this.documentService.downloadOutgoingDocumentFile(clientId, sharedDocument.reference).subscribe({
        next: (data) => {
          FileSaver.saveAs(new Blob([data], { type: sharedDocument.documentMimeType }), sharedDocument.fileName);
          this.confirmDocumentOpened(clientId, direction, sharedDocument);
        },
        error: (error) => {
          console.error('[SHARED-DOCS] Downloading shared document failed', error);
          this.notifyDownloadFailed();
        },
      });
      return;
    }

    console.error('[SHARED-DOCS] Document has no inline data and no reference', {
      documentShareId: sharedDocument.documentShareId,
      reference: sharedDocument.reference,
      folder: this.currentFolder?.key,
    });
    this.notifyDownloadFailed();
  }

  private saveInlineDocument(sharedDocument: LundifySharedDocument) {
    try {
      const bytes = this.decodeBase64(sharedDocument.data);
      FileSaver.saveAs(new Blob([bytes], { type: sharedDocument.documentMimeType }), sharedDocument.fileName);
    } catch (error) {
      console.error('[SHARED-DOCS] Saving inline document failed', error);
      this.notifyDownloadFailed();
    }
  }

  private confirmDocumentOpened(
    clientId: number | undefined,
    direction: SharedFolderKey | null,
    sharedDocument: LundifySharedDocument,
  ) {
    if (clientId === undefined || direction !== 'incoming') {
      return;
    }

    this.documentService.markIncomingDocumentOpened(clientId, sharedDocument.documentShareId).subscribe({
      next: () => {
        if (this.client?.id === clientId && this.currentFolder?.key === direction) {
          this.loadFolder();
        }
      },
      error: (error) => {
        // The download itself already succeeded, so the user is not interrupted with a toast,
        // but the failure must stay visible in the console.
        console.error('[SHARED-DOCS] Confirming document opened failed', error);
      },
    });
  }

  private notifyDownloadFailed() {
    this.store.dispatch(
      ToastActions.showErrorMessage({
        summary: 'Ett fel har uppstått',
        detail: 'Filen kunde inte sparas. Försök igen eller kontakta Support.',
      }),
    );
  }

  private loadDirection(request: Observable<LundifySharedDocument[]>) {
    this.subscribeToList(request, (documents) => {
      this.documents = documents;
    });
  }

  private loadArchived() {
    if (!this.client) {
      this.loading = false;
      return;
    }

    const clientId = this.client.id;
    this.subscribeToList(
      forkJoin({
        outgoing: this.getAllSharedDocuments((page) =>
          this.documentService.getOutgoingSharedDocuments(clientId, 'ARCHIVED', page),
        ),
        incoming: this.getAllSharedDocuments((page) =>
          this.documentService.getIncomingSharedDocuments(clientId, 'ARCHIVED', page),
        ),
      }),
      ({ outgoing, incoming }) => {
        this.documents = [...outgoing, ...incoming];
      },
    );
  }

  private archiveDocument(sharedDocument: LundifySharedDocument) {
    if (!this.client) {
      return;
    }

    this.documentService.archiveSharedDocument(this.client.id, sharedDocument.documentShareId).subscribe({
      next: () => {
        this.documents = this.documents.filter((doc) => doc.documentShareId !== sharedDocument.documentShareId);
        this.store.dispatch(
          ToastActions.showInfoMessage({
            summary: 'Dokumentet har arkiverats',
            detail: 'Dokumentet finns nu i mappen Arkiverade dokument.',
          }),
        );
      },
      error: (error) => {
        console.error('[SHARED-DOCS] Archiving document failed', error);
      },
    });
  }

  // The shared catchErrorAndShowMessage pipe can complete without emitting (ignorable errors), so
  // "loading" is reset in finalize rather than in next/error. A newer load cancels the previous
  // one so a late response cannot overwrite the folder the user switched to.
  private subscribeToList<T>(source: Observable<T>, onResult: (result: T) => void) {
    this.listSubscription?.unsubscribe();
    this.badgeCache = new WeakMap();
    this.menuItemsCache = new WeakMap();
    this.listSubscription = source.pipe(finalize(() => (this.loading = false))).subscribe({
      next: onResult,
      error: (error) => {
        // The service pipe already shows the user-facing toast for known errors; this keeps every
        // failure visible in the console as well.
        console.error('[SHARED-DOCS] Loading shared documents failed', error);
        this.documents = [];
      },
    });
  }

  private decodeBase64(data: string): Uint8Array {
    const binary = atob(data);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }
}